import '@data/Simulations/pipeline/blocks/PvPotentialsBlock';
import type { PvPotentialsBlockParameters } from '@data/Simulations/pipeline/blocks/PvPotentialsBlock';
import { BlockKeys } from '@data/Simulations/pipeline/BlockCatalog';
import { GroupLabel } from '@gui/components/DesignSchemes/DesignSchemeView';
import { getSentence } from '@gui/constants/sentences';
import PvDemandPeaksDialog, { type PvDemandPeakTargetOption } from '../dialogs/PvDemandPeaksDialog';
import { ControllerManager } from '@gui/controller/ControllerManager';
import usePersistentStore, { getGlobalState, getLayersState, useGlobalBucket } from '@gui/store/persistent/persistentStore';
import { Box, MenuItem, Select, Slider, Tooltip, Typography, useTheme } from '@mui/material';
import { CheckCircle, InfoCircle, XCircle } from '@untitledui-pro/icons/line';
import {
    PHOTOVOLTAIC_STRATEGIES,
    SIM_SUN4D,
    type BlockParameters,
    type DeepPartial,
    type PeakController,
    type PhotovoltaicStrategy,
    type SolarEnergyResourcesState,
} from '@visualizer/types';
import { Panels } from '@gui/constants/Panels';
import { SOLAR_MONTH_TO_FRAME } from '@gui/constants/SolarMonths';
import { computeTotalEnergyFromSensors } from 'Utils/EnergyUtils';
import { formatNumberWithApostrophes } from 'Utils/MetricFormatting';
import { getGrid } from 'store/LoadingStore';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePipelineBlockParams } from './usePipelineBlockParams';

type PvPotentialsParametersProps = {
    blockId: string;
};

const USER_GUIDE_URL = 'https://urbanmetrix.com/guide/use-the-visualizer/filters#pv-supply';
const FormatValue = (value: number): string => formatNumberWithApostrophes(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const getPvPotentialsDefaultParams = (): Pick<
    PvPotentialsBlockParameters,
    'strategy' | 'demand' | 'panelEfficiency' | 'surfaceCoverage' | 'tolerance' | 'maxIterations'
> => ({
    strategy: PHOTOVOLTAIC_STRATEGIES.MATCH_YEARLY_DEMAND,
    demand: 0,
    panelEfficiency: 20,
    surfaceCoverage: 100,
    tolerance: 1000,
    maxIterations: 20,
});

// I Know this part is not beautiful, but the parsing of the peaks into a single aggregated total must be done like this for now:
const parsePeakValue = (value: string): number => {
    return Number(
        String(value ?? '')
            .replace(',', '.')
            .trim()
    );
};

const aggregateTotalPeaks = (heating: PeakController, cooling: PeakController, appliances: PeakController): PeakController => {
    const controllers = [heating, cooling, appliances];
    const accumulator: Record<string, Record<string, number>> = {};

    for (const controller of controllers) {
        let currentType = '';
        for (let i = 0; i < controller.type.length; i++) {
            const type = controller.type[i];
            if (type && type.trim() !== '') {
                currentType = type;
            }
            const occurrence = controller.occurrences[i];
            const value = parsePeakValue(controller.values[i]);
            if (!currentType || !occurrence || !Number.isFinite(value)) continue;

            if (!accumulator[currentType]) accumulator[currentType] = {};
            accumulator[currentType][occurrence] = (accumulator[currentType][occurrence] ?? 0) + value;
        }
    }

    const total: PeakController = { type: [], occurrences: [], values: [] };
    const monthlyOccurrences = Object.keys(accumulator.Monthly ?? {}).sort(
        (a, b) =>
            (SOLAR_MONTH_TO_FRAME[a as keyof typeof SOLAR_MONTH_TO_FRAME] ?? 0) -
            (SOLAR_MONTH_TO_FRAME[b as keyof typeof SOLAR_MONTH_TO_FRAME] ?? 0)
    );
    monthlyOccurrences.forEach((occ, i) => {
        total.type.push(i === 0 ? 'Monthly' : '');
        total.occurrences.push(occ);
        total.values.push((accumulator.Monthly[occ] ?? 0).toFixed(2));
    });

    const hourlyOccurrences = Object.keys(accumulator.Hourly ?? {}).sort((a, b) => Number(a) - Number(b));
    hourlyOccurrences.forEach((occ, i) => {
        total.type.push(i === 0 ? 'Hourly' : '');
        total.occurrences.push(occ);
        total.values.push((accumulator.Hourly[occ] ?? 0).toFixed(2));
    });

    return total;
};

const splitTotalPeaks = (totalPeaks: PeakController) => {
    let currentType = '';
    const monthly: Array<{ occurrence: string; rate: number }> = [];
    const hourly: Array<{ hour: number; rate: number }> = [];

    for (let i = 0; i < totalPeaks.occurrences.length; i++) {
        const typeEntry = totalPeaks.type[i];
        if (typeEntry && typeEntry.trim() !== '') {
            currentType = typeEntry.trim();
        }
        const occurrence = totalPeaks.occurrences[i];
        const rate = parsePeakValue(totalPeaks.values[i]);
        if (!Number.isFinite(rate) || rate <= 0 || !occurrence) continue;

        if (currentType === 'Monthly' && occurrence in SOLAR_MONTH_TO_FRAME) {
            monthly.push({ occurrence, rate });
            continue;
        }
        if (currentType === 'Hourly') {
            const hour = Number(occurrence);
            if (Number.isFinite(hour) && hour >= 0 && hour <= 23) {
                hourly.push({ hour, rate });
            }
        }
    }

    monthly.sort(
        (a, b) =>
            (SOLAR_MONTH_TO_FRAME[a.occurrence as keyof typeof SOLAR_MONTH_TO_FRAME] ?? 0) -
            (SOLAR_MONTH_TO_FRAME[b.occurrence as keyof typeof SOLAR_MONTH_TO_FRAME] ?? 0)
    );
    hourly.sort((a, b) => a.hour - b.hour);
    return { monthly, hourly };
};

const getPvStrategyTooltip = (strategy: PhotovoltaicStrategy): React.ReactNode => {
    const descriptionByStrategy: Record<PhotovoltaicStrategy, string> = {
        [PHOTOVOLTAIC_STRATEGIES.MAX_YEARLY_PRODUCTION]: getSentence('Pipeline.Filters.PvPotentials.MaxYearlyProduction.Tooltip'),
        [PHOTOVOLTAIC_STRATEGIES.MATCH_YEARLY_DEMAND]: getSentence('Pipeline.Filters.PvPotentials.MatchYearlyDemand.Tooltip'),
        [PHOTOVOLTAIC_STRATEGIES.MAX_DEMAND_PEAKS]: getSentence('Pipeline.Filters.PvPotentials.MaxDemandPeaks.Tooltip'),
    };

    return (
        <Typography
            sx={{
                fontSize: '10.5px',
                lineHeight: 1.4,
                color: 'text.secondary',
            }}
        >
            {descriptionByStrategy[strategy]}{' '}
            <a href={USER_GUIDE_URL} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                Learn more
            </a>
        </Typography>
    );
};

const renderPvStrategyMenuLabel = (label: string, strategy: PhotovoltaicStrategy) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
        <Tooltip placement="top" title={getPvStrategyTooltip(strategy)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}>
                <InfoCircle className="icon-size-small" />
            </span>
        </Tooltip>
        <Typography sx={{ fontSize: '10px' }}>{label}</Typography>
    </Box>
);

const PvPotentialsParameters: React.FC<PvPotentialsParametersProps> = ({ blockId }) => {
    const theme = useTheme();
    const isLightTheme = theme.palette.mode === 'light';
    const matchedColor = isLightTheme ? '#1f7a1f' : '#00ff00';
    const unmatchedColor = isLightTheme ? '#b42318' : '#ff0000';

    // Controllers:
    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);

    // Zustand variables:
    const params = usePipelineBlockParams<PvPotentialsBlockParameters>(blockId);
    const { photovoltaicPotentials, yearlyDemand, demandPeaks } = useGlobalBucket((s) => s.solarEnergyResources);
    const isMatchingDemand = useGlobalBucket((s) => s.solarEnergyResources.solarMatching.isMatchingDemand);
    const hasPipelineExecuted = usePersistentStore((state) => state.getActiveLayerState().pipeline.executionCount);
    const setPanel = usePersistentStore((s) => s.setPanel);
    const setSolarEnergyPanel = (updates: DeepPartial<SolarEnergyResourcesState>) => setPanel(updates, Panels.SOLAR_ENERGY_RESOURCES);
    const setCurrentLayerId = usePersistentStore((s) => s.setCurrentLayerId);
    const updateLayerState = usePersistentStore((s) => s.updateLayerState);

    // React State:
    const [isDemandPeaksDialogOpen, setIsDemandPeaksDialogOpen] = useState(false);
    const [demandPeaksOptions, setDemandPeaksOptions] = useState<PvDemandPeakTargetOption[]>([]);
    const lastSyncedTotalPeaksRef = useRef<string>('');

    // Other variables:
    const strategyOptions: PhotovoltaicStrategy[] = [
        PHOTOVOLTAIC_STRATEGIES.MAX_YEARLY_PRODUCTION,
        PHOTOVOLTAIC_STRATEGIES.MATCH_YEARLY_DEMAND,
        PHOTOVOLTAIC_STRATEGIES.MAX_DEMAND_PEAKS,
    ];

    const buildDemandPeakOptions = (): PvDemandPeakTargetOption[] => {
        const { buildingPatterns, demandPeaks } = getGlobalState().solarEnergyResources;
        const floorArea = Math.max(0, Number(buildingPatterns.grossFloorSurface.value ?? 0));
        if (floorArea <= 0) return [];

        // Retrieve monthly & hourly layers:
        const sun4dLayers = getGrid().findLayersByKind(SIM_SUN4D);
        const monthlyLayers = sun4dLayers.filter((layer) => layer.getFrameCollection().length === 12);
        const hourlyLayers = sun4dLayers.filter((layer) => layer.getFrameCollection().length === 24);
        const options: PvDemandPeakTargetOption[] = [];

        const totalPeaks =
            demandPeaks.totalPeaks.occurrences.length > 0
                ? demandPeaks.totalPeaks
                : aggregateTotalPeaks(demandPeaks.heatingPeaks, demandPeaks.coolingPeaks, demandPeaks.appliancesPeaks);
        const { monthly, hourly } = splitTotalPeaks(totalPeaks);
        const winterMonthly = monthly[0];
        const summerMonthly = monthly[monthly.length - 1];

        for (const layer of monthlyLayers) {
            if (winterMonthly) {
                const frame = SOLAR_MONTH_TO_FRAME[winterMonthly.occurrence as keyof typeof SOLAR_MONTH_TO_FRAME] ?? 0;
                options.push({
                    id: `${layer.getUID()}-monthly-winter-${frame}`,
                    layerId: layer.getUID(),
                    layerName: layer.name,
                    simSetName: layer.parentSimulationSet.name,
                    frame,
                    peakLabel: `Heating Peak (${winterMonthly.occurrence})`,
                    peakRate: winterMonthly.rate,
                    demand: floorArea * winterMonthly.rate,
                });
            }
            if (summerMonthly && summerMonthly.occurrence !== winterMonthly?.occurrence) {
                const frame = SOLAR_MONTH_TO_FRAME[summerMonthly.occurrence as keyof typeof SOLAR_MONTH_TO_FRAME] ?? 6;
                options.push({
                    id: `${layer.getUID()}-monthly-summer-${frame}`,
                    layerId: layer.getUID(),
                    layerName: layer.name,
                    simSetName: layer.parentSimulationSet.name,
                    frame,
                    peakLabel: `Cooling Peak (${summerMonthly.occurrence})`,
                    peakRate: summerMonthly.rate,
                    demand: floorArea * summerMonthly.rate,
                });
            }
        }

        const morning = hourly[0] ?? { hour: 7, rate: 0 };
        const evening = hourly[hourly.length - 1] ?? { hour: 18, rate: 0 };

        for (const layer of hourlyLayers) {
            if (morning.rate > 0) {
                options.push({
                    id: `${layer.getUID()}-hourly-morning-${morning.hour}`,
                    layerId: layer.getUID(),
                    layerName: layer.name,
                    simSetName: layer.parentSimulationSet.name,
                    frame: morning.hour,
                    peakLabel: `Morning Peak (${morning.hour}:00)`,
                    peakRate: morning.rate,
                    demand: floorArea * morning.rate,
                });
            }
            if (evening.rate > 0 && evening.hour !== morning.hour) {
                options.push({
                    id: `${layer.getUID()}-hourly-evening-${evening.hour}`,
                    layerId: layer.getUID(),
                    layerName: layer.name,
                    simSetName: layer.parentSimulationSet.name,
                    frame: evening.hour,
                    peakLabel: `Evening Peak (${evening.hour}:00)`,
                    peakRate: evening.rate,
                    demand: floorArea * evening.rate,
                });
            }
        }

        return options;
    };

    // Handlers:
    const updateParams = (partial: Partial<PvPotentialsBlockParameters>) => {
        pipelineController.updateBlockParams(blockId, partial as BlockParameters);
    };

    // Effects:
    useEffect(() => {
        const defaults = getPvPotentialsDefaultParams();
        if (!params) {
            updateParams(defaults);
            return;
        }

        const missing: Partial<PvPotentialsBlockParameters> = {};
        if (params.strategy == null) missing.strategy = defaults.strategy;
        if (params.demand == null) missing.demand = defaults.demand;
        if (params.panelEfficiency == null) missing.panelEfficiency = defaults.panelEfficiency;
        if (params.surfaceCoverage == null) missing.surfaceCoverage = defaults.surfaceCoverage;
        if (params.tolerance == null) missing.tolerance = defaults.tolerance;
        if (params.maxIterations == null) missing.maxIterations = defaults.maxIterations;

        if (Object.keys(missing).length > 0) {
            updateParams(missing);
        }
    }, [params]);

    // Keep totalPeaks synced with heating/cooling/appliances:
    useEffect(() => {
        const computedTotal = aggregateTotalPeaks(demandPeaks.heatingPeaks, demandPeaks.coolingPeaks, demandPeaks.appliancesPeaks);
        const computedSerialized = JSON.stringify(computedTotal);
        const storeSerialized = JSON.stringify(demandPeaks.totalPeaks);
        if (computedSerialized === storeSerialized) return;
        if (computedSerialized === lastSyncedTotalPeaksRef.current) return;
        lastSyncedTotalPeaksRef.current = computedSerialized;
        setSolarEnergyPanel({
            demandPeaks: {
                totalPeaks: computedTotal,
            },
        });
    }, [demandPeaks.heatingPeaks, demandPeaks.coolingPeaks, demandPeaks.appliancesPeaks, demandPeaks.totalPeaks]);

    // Update yearly demand if needed:
    const totalYearlyDemand = Math.max(0, Number(yearlyDemand ?? 0));
    useEffect(() => {
        if (!params) return;
        if ((params.strategy ?? PHOTOVOLTAIC_STRATEGIES.MATCH_YEARLY_DEMAND) !== PHOTOVOLTAIC_STRATEGIES.MATCH_YEARLY_DEMAND) return;
        if (params.demand !== totalYearlyDemand) updateParams({ demand: totalYearlyDemand });
    }, [params?.strategy, params?.demand, totalYearlyDemand]);

    if (!params) return null;

    // Compute current supply when params changes:
    const panelEfficiency = params.panelEfficiency ?? photovoltaicPotentials.panelEfficiency?.value ?? 100;
    const surfaceCoverage = params.surfaceCoverage ?? photovoltaicPotentials.surfaceCoverage?.value ?? 100;
    const currentStrategy = params.strategy ?? PHOTOVOLTAIC_STRATEGIES.MATCH_YEARLY_DEMAND;
    const demandToMatch =
        currentStrategy === PHOTOVOLTAIC_STRATEGIES.MAX_DEMAND_PEAKS ? Math.max(0, Number(params.demand ?? 0)) : totalYearlyDemand;
    const sensorsValue = useMemo(() => {
        const result = pipelineController.getLastResult();
        if (!result?.data) return undefined;
        return computeTotalEnergyFromSensors(result.data);
    }, [pipelineController, hasPipelineExecuted]);
    const totalEnergySupply =
        sensorsValue !== undefined
            ? sensorsValue * (panelEfficiency / 100) * (surfaceCoverage / 100)
            : (photovoltaicPotentials.totalEnergySupply ?? 0);

    // Hide/Show different things based on current strategy:
    const isMatchDemandStrategy =
        currentStrategy === PHOTOVOLTAIC_STRATEGIES.MATCH_YEARLY_DEMAND || currentStrategy === PHOTOVOLTAIC_STRATEGIES.MAX_DEMAND_PEAKS;
    const showMatchStatus = isMatchDemandStrategy && hasPipelineExecuted > 0 && !isMatchingDemand && demandToMatch > 0;
    const isDemandMatched = showMatchStatus && totalEnergySupply >= demandToMatch;

    // UI:
    const tinyLabelSx = {
        fontSize: '10px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    };
    const rowLabelSx = {
        fontSize: '10px',
        flex: 1,
        minWidth: 0,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        lineHeight: 1.2,
    };
    const rightFieldSx = {
        ml: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '60%',
        minWidth: 96,
        maxWidth: '100%',
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ width: '100%' }}>
                <Typography sx={{ ...rowLabelSx, textAlign: 'center' }}>Strategy</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.35 }}>
                    <Select
                        size="small"
                        value={(params.strategy ?? PHOTOVOLTAIC_STRATEGIES.MATCH_YEARLY_DEMAND) as PhotovoltaicStrategy}
                        renderValue={(selected) => String(selected)}
                        onChange={(e) => {
                            const nextStrategy = e.target.value as PhotovoltaicStrategy;
                            if (nextStrategy !== PHOTOVOLTAIC_STRATEGIES.MAX_DEMAND_PEAKS) {
                                updateParams({ strategy: nextStrategy });
                                return;
                            }
                            const options = buildDemandPeakOptions();
                            if (options.length === 0) {
                                updateParams({ strategy: PHOTOVOLTAIC_STRATEGIES.MAX_DEMAND_PEAKS });
                                return;
                            }
                            setDemandPeaksOptions(options);
                            setIsDemandPeaksDialogOpen(true);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        MenuProps={{
                            PaperProps: {
                                onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
                                onClick: (e: React.MouseEvent) => e.stopPropagation(),
                            },
                            MenuListProps: {
                                onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
                                onClick: (e: React.MouseEvent) => e.stopPropagation(),
                            },
                        }}
                        sx={{
                            width: '65%',
                            minWidth: 160,
                            '& .MuiInputBase-root, &.MuiInputBase-root': {
                                height: 22,
                                fontSize: '10px',
                            },
                            '& .MuiSelect-select': {
                                py: 0.25,
                            },
                        }}
                    >
                        {strategyOptions.map((option) => (
                            <MenuItem key={option} value={option} sx={{ fontSize: '10px' }}>
                                {renderPvStrategyMenuLabel(String(option), option)}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Box>

            {isMatchDemandStrategy && (
                <Box sx={{ width: '100%', mt: 0.75 }}>
                    <Typography sx={{ ...rowLabelSx, textAlign: 'center' }}>Demand to be matched</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.35 }}>
                        <Typography sx={{ ...tinyLabelSx, fontWeight: 600, fontSize: '11px' }}>{FormatValue(demandToMatch)}</Typography>
                        <Typography sx={tinyLabelSx}>[kWh/y]</Typography>
                    </Box>
                </Box>
            )}

            <GroupLabel label="Inputs" mt={0.75} />

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', mt: 0.75 }}>
                <Typography sx={rowLabelSx}>Panel Efficiency</Typography>
                <Box sx={{ ...rightFieldSx, mt: 0.1 }}>
                    <Slider
                        className="panel-slider"
                        size="small"
                        value={panelEfficiency}
                        min={photovoltaicPotentials.panelEfficiency?.minValue ?? 0}
                        max={photovoltaicPotentials.panelEfficiency?.maxValue ?? 100}
                        step={1}
                        onChange={(_, val) => updateParams({ panelEfficiency: Array.isArray(val) ? val[0] : val })}
                        valueLabelDisplay="on"
                        sx={{ flex: 1, minWidth: 0 }}
                    />
                    <Typography sx={tinyLabelSx}>[%]</Typography>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', mt: 0.75 }}>
                <Typography sx={rowLabelSx}>Surface Coverage</Typography>
                <Box sx={{ ...rightFieldSx, mt: 0.1 }}>
                    <Slider
                        className="panel-slider"
                        size="small"
                        value={surfaceCoverage}
                        min={photovoltaicPotentials.surfaceCoverage?.minValue ?? 0}
                        max={photovoltaicPotentials.surfaceCoverage?.maxValue ?? 100}
                        step={1}
                        onChange={(_, val) => updateParams({ surfaceCoverage: Array.isArray(val) ? val[0] : val })}
                        valueLabelDisplay="on"
                        sx={{ flex: 1, minWidth: 0 }}
                    />
                    <Typography sx={tinyLabelSx}>[%]</Typography>
                </Box>
            </Box>

            <GroupLabel label="Results" mt={0.75} />

            <Box sx={{ width: '100%', mt: 0.75 }}>
                <Typography sx={{ ...rowLabelSx, textAlign: 'center' }}>Total Energy Supply</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.35 }}>
                    <Typography sx={{ ...tinyLabelSx, fontWeight: 600, fontSize: '11px' }}>{FormatValue(totalEnergySupply)}</Typography>
                    <Typography sx={tinyLabelSx}>[kWh/y]</Typography>
                </Box>
            </Box>

            {showMatchStatus && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mt: 0.75, width: '100%' }}>
                    {isDemandMatched ? (
                        <>
                            <CheckCircle color={matchedColor} style={{ width: 14, height: 14 }} />
                            <Typography sx={{ ...tinyLabelSx, color: matchedColor }}>Demand was matched</Typography>
                        </>
                    ) : (
                        <>
                            <XCircle color={unmatchedColor} style={{ width: 14, height: 14 }} />
                            <Typography sx={{ ...tinyLabelSx, color: unmatchedColor }}>Demand wasn't matched</Typography>
                        </>
                    )}
                </Box>
            )}

            {/* Popup for the Demand Peaks Match layer (and frame) selection */}
            <PvDemandPeaksDialog
                open={isDemandPeaksDialogOpen}
                options={demandPeaksOptions}
                onCancel={() => {
                    setIsDemandPeaksDialogOpen(false);
                    setDemandPeaksOptions([]);
                }}
                onConfirm={(selected) => {
                    const previousActiveLayer = getLayersState().activeLayerID;
                    const baseDefaults = getPvPotentialsDefaultParams();

                    // Add a PV Block with the correct options to all target layers that have been selected:
                    for (const target of selected) {
                        setCurrentLayerId(target.layerId);
                        updateLayerState(target.layerId, { segments: { isAggregate: false, frameSelected: target.frame } });

                        const layerBlocks = getLayersState().layerStates[target.layerId]?.pipeline?.blocks ?? [];
                        const existingPvBlocks = layerBlocks.filter((b) => b.type === BlockKeys.PvPotentials);

                        const perLayerParams: Partial<PvPotentialsBlockParameters> = {
                            strategy: PHOTOVOLTAIC_STRATEGIES.MAX_DEMAND_PEAKS,
                            demand: target.demand,
                            peakTargets: [target],
                            panelEfficiency: params?.panelEfficiency ?? baseDefaults.panelEfficiency,
                            surfaceCoverage: params?.surfaceCoverage ?? baseDefaults.surfaceCoverage,
                            tolerance: params?.tolerance ?? baseDefaults.tolerance,
                            maxIterations: params?.maxIterations ?? baseDefaults.maxIterations,
                        };

                        existingPvBlocks.forEach((block) => pipelineController.removeBlock(block.id));
                        pipelineController.addBlock(BlockKeys.PvPotentials, {
                            enabled: true,
                            ...baseDefaults,
                            ...perLayerParams,
                        } as BlockParameters);

                        // Forces layer to refresh even if not directly display, before going into mixer:
                        const layer = getGrid().searchLayerByUID(target.layerId);
                        layer.getPipeline().clearCacheAll();
                        layer.runPipeline({ suppressNotifications: true, octTree: getGrid().octTree! });
                    }

                    if (selected[0]) {
                        setCurrentLayerId(selected[0].layerId);
                    } else if (previousActiveLayer) {
                        setCurrentLayerId(previousActiveLayer);
                    }

                    // Add items to mixer & activate multi if not already on:
                    const mixerController = ControllerManager.getInstance().getMixerController();
                    const uniqueLayerIds = Array.from(new Set(selected.map((item) => item.layerId)));
                    setPanel({ isMixerMode: true, multimode: true, mixerAndLogic: false }, Panels.DATA);
                    mixerController.deleteAllLayers();
                    uniqueLayerIds.forEach((layerId) => mixerController.addLayer(layerId));
                    mixerController.run();

                    // Done:
                    setIsDemandPeaksDialogOpen(false);
                    setDemandPeaksOptions([]);
                }}
            />
        </Box>
    );
};

export default PvPotentialsParameters;
